/**
 * Classify a fetch/axios failure into a human-readable bucket and
 * give the user "what's happening + what to do" guidance.
 *
 * Returns:
 *   {
 *     classification,    // machine label e.g. "cloudflare_520"
 *     status,            // HTTP status code or 0 for network errors
 *     title,             // short user-facing headline
 *     detail,            // 1–2 sentence plain-language explanation
 *     advice,            // what the user can do right now
 *     transient,         // true if retrying may help
 *   }
 */
export function classifyServiceError(errOrStatus, opts = {}) {
  // Accept either an axios/fetch error object, a Response, or a raw status.
  let status = 0;
  if (typeof errOrStatus === "number") {
    status = errOrStatus;
  } else if (errOrStatus && typeof errOrStatus === "object") {
    status = errOrStatus.status
      || errOrStatus.response?.status
      || (errOrStatus.request ? 0 : 0);
  }
  const ja = !!opts.ja;

  // ── Cloudflare-origin failures (520-526) ──────────────────────
  if (status >= 520 && status <= 526) {
    const cfMap = {
      520: ja
        ? { title: "サーバーが応答していません", detail: "Cloudflareがアプリのサーバーに到達できませんでした。バックエンドの再起動中の可能性があります。" }
        : { title: "Server is not responding", detail: "Cloudflare couldn't reach the app's origin server. The backend may be restarting." },
      521: ja
        ? { title: "オリジンサーバー停止中", detail: "アプリのバックエンドが現在オフラインです。" }
        : { title: "Origin server is down", detail: "The app's backend is currently offline." },
      522: ja
        ? { title: "接続がタイムアウトしました", detail: "サーバーへの接続に時間がかかりすぎました。" }
        : { title: "Connection timed out", detail: "The connection to the server took too long." },
      523: ja
        ? { title: "サーバーに到達できません", detail: "ネットワーク経路に問題があります。" }
        : { title: "Origin is unreachable", detail: "A network routing problem is preventing the connection." },
      524: ja
        ? { title: "サーバーの応答が遅延しています", detail: "サーバーは受信しましたが、応答が間に合いませんでした。" }
        : { title: "Server response was too slow", detail: "The server accepted the request but didn't respond in time." },
    };
    const e = cfMap[status] || cfMap[520];
    return {
      classification: `cloudflare_${status}`,
      status,
      title: e.title,
      detail: e.detail,
      advice: ja
        ? "30秒ほどお待ちいただき、もう一度お試しください。問題が続く場合は support@japantollreceipts.jp までご連絡ください。"
        : "Please wait about 30 seconds and try again. If the issue persists, contact support@japantollreceipts.jp.",
      transient: true,
    };
  }

  // ── Gateway failures (502 / 504) — real proxy / worker crash ──
  if (status === 502 || status === 504) {
    return {
      classification: `gateway_${status}`,
      status,
      title: ja ? "一時的にサービスが利用できません" : "Service is temporarily unavailable",
      detail: ja
        ? "アプリは現在再起動またはデプロイ中の可能性があります。通常1分以内に復旧します。"
        : "The app may be restarting or deploying. This usually recovers within a minute.",
      advice: ja
        ? "少し待ってからもう一度お試しください。"
        : "Please wait a moment and try again.",
      transient: true,
    };
  }

  // ── Planned unavailability (503) ──────────────────────────────
  // Iter 323 — 503 is treated as a friendly "feature paused" state,
  // not a gateway crash. Used by /api/user/meisai/pull-records-now
  // when Chromium can't launch.
  if (status === 503) {
    return {
      classification: "service_unavailable",
      status,
      title: ja ? "現在この機能は一時停止中です" : "This feature is temporarily paused",
      detail: ja
        ? "メンテナンスまたは外部サービスの修復中のため、一時的に利用できません。"
        : "We're repairing an underlying service. Please try again shortly.",
      advice: ja
        ? "数分後に再度お試しください。"
        : "Please try again in a few minutes.",
      transient: true,
    };
  }

  // ── Network layer / no status ─────────────────────────────────
  if (!status || status === 0) {
    return {
      classification: "network",
      status: 0,
      title: ja ? "ネットワークに接続できません" : "Network connection failed",
      detail: ja
        ? "サーバーに接続できませんでした。インターネット接続をご確認ください。"
        : "Your browser couldn't reach the server. Please check your internet connection.",
      advice: ja
        ? "Wi-Fiを確認するか、しばらくしてからもう一度お試しください。"
        : "Check your Wi-Fi or try again in a moment.",
      transient: true,
    };
  }

  // ── Other 5xx ─────────────────────────────────────────────────
  if (status >= 500) {
    return {
      classification: "server_5xx",
      status,
      title: ja ? "サーバーでエラーが発生しました" : "The server hit an error",
      detail: ja
        ? "予期しない問題が発生しました。技術チームに通知済みです。"
        : "An unexpected problem occurred handling your request. The technical team has been notified.",
      advice: ja
        ? "少し待ってからもう一度お試しください。"
        : "Please wait a moment and try again.",
      transient: true,
    };
  }

  // ── 4xx — not a "site down" — fall through ────────────────────
  return {
    classification: status === 429 ? "rate_limited" : "client_error",
    status,
    title: ja ? "リクエストを処理できませんでした" : "Couldn't process the request",
    detail: ja
      ? "リクエストに問題がありました。"
      : "There was a problem with the request.",
    advice: ja
      ? "入力内容をご確認ください。"
      : "Please check your input.",
    transient: status === 429,
  };
}
